import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useReviewActions, useReviews } from "../../hook/Usereviewgarage";
import ReviewActionDialog from "../garage-reviews/Reviewactiondialog";

// ---------------------------------------------------------------------------
// Small helpers / pieces
// ---------------------------------------------------------------------------
const AVATAR_COLORS = [
  "#1d4ed8",
  "#92400e",
  "#15803d",
  "#6d28d9",
  "#0e7490",
  "#be185d",
];

const getInitials = (name = "") => {
  const parts = name
    .replace(/[^\p{L}\s]/gu, "")
    .trim()
    .split(/\s+/);
  const letters =
    parts.length > 1
      ? parts[0][0] + parts[parts.length - 1][0]
      : (parts[0] || "?").slice(0, 2);
  return letters.toUpperCase();
};

const chipTones = {
  success: { background: "#e8f7ee", color: "#1b7a46", border: "#bfe6cf" },
  danger: { background: "#fdecec", color: "#b42318", border: "#f5c2c0" },
  warning: { background: "#fff3d6", color: "#9a6700", border: "#f3d98b" },
};

const Chip = ({ tone = "warning", icon, children }) => {
  const t = chipTones[tone];
  return (
    <span
      className="d-inline-flex align-items-center gap-1"
      style={{
        fontSize: 11,
        fontWeight: 500,
        padding: "2px 8px",
        borderRadius: 4,
        background: t.background,
        color: t.color,
        border: `1px solid ${t.border}`,
      }}
    >
      {icon && <Icon icon={icon} />}
      {children}
    </span>
  );
};

const Stars = ({ value = 0, size = 13 }) => {
  const rounded = Math.max(0, Math.min(5, Math.round(Number(value) || 0)));

  return (
    <span
      className="text-nowrap"
      style={{ color: "#f5a524", fontSize: size, letterSpacing: 1 }}
      title={`${value} / 5`}
    >
      {"★".repeat(rounded)}
      <span style={{ color: "#d6d9df" }}>{"★".repeat(5 - rounded)}</span>
    </span>
  );
};

const StatCard = ({ children, highlight = false }) => (
  <div
    className="h-100"
    style={{
      padding: "14px 16px",
      borderRadius: 8,
      border: `1px solid ${highlight ? "#f6e2b3" : "#e9ecef"}`,
      background: highlight ? "#fff6df" : "#fff",
    }}
  >
    {children}
  </div>
);

const formatDate = (date, dateFormat = "dd MMM, yyyy") => {
  if (!date) return "-";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";
  return format(parsed, dateFormat);
};

// Flags come from moderation data (array of flags or a single flag reason).
const getFlags = (review) => {
  const moderation = review.moderation || {};

  if (Array.isArray(moderation.flags) && moderation.flags.length > 0) {
    return moderation.flags
      .map((flag) =>
        typeof flag === "string" ? flag : flag?.label || flag?.reason,
      )
      .filter(Boolean);
  }

  if (moderation.flag_reason) return [String(moderation.flag_reason)];

  return moderation.status === "flagged" ? ["Flagged for moderation"] : [];
};

const getBookingTone = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "completed") return "success";
  if (s === "cancelled" || s === "canceled") return "danger";
  return "warning";
};

const getRemovedBy = (removedBy) => {
  if (removedBy == null) return "Admin";
  if (typeof removedBy === "object") {
    return removedBy.name || removedBy.fullName || removedBy.id || "Admin";
  }
  return removedBy;
};

// API status "visible" is shown as "Published" in the UI.
const statusChip = {
  visible: { tone: "success", label: "Published" },
  flagged: { tone: "warning", label: "Flagged" },
  removed: { tone: "danger", label: "Removed" },
};

// ---------------------------------------------------------------------------
// Reviews section (placed on the View Garage page)
// ---------------------------------------------------------------------------
const GarageReviewsSection = ({ garageId }) => {
  const navigate = useNavigate();

  // all | published | flagged | removed
  const [activeTab, setActiveTab] = useState("all");

  // { type: "remove" | "restore", reviewId, reasons }
  const [dialog, setDialog] = useState(null);

  const { records, loading, refresh } = useReviews({
    page: 0,
    limit: 100,
    query: "",
    status: "",
    garageId: garageId ? String(garageId) : "",
    userId: "",
    rating: "",
    onError: (message) => toast.error(message),
  });

  const { actionLoadingId, removeReview, restoreReview, fetchRemovalReasons } =
    useReviewActions();

  const getStatus = (review) => review.moderation?.status || "visible";

  const published = records.filter((r) => getStatus(r) === "visible");
  const flagged = records.filter((r) => getStatus(r) === "flagged");
  const removed = records.filter((r) => getStatus(r) === "removed");

  // Average over everything that is still on the public profile.
  const publicReviews = records.filter((r) => getStatus(r) !== "removed");
  const average = publicReviews.length
    ? publicReviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) /
      publicReviews.length
    : 0;

  const listByTab = {
    all: records,
    published,
    flagged,
    removed,
  };

  const visible = [...listByTab[activeTab]].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const tabs = [
    { value: "all", label: "All", count: records.length },
    { value: "published", label: "Published", count: published.length },
    { value: "flagged", label: "Flagged", count: flagged.length },
    { value: "removed", label: "Removed", count: removed.length },
  ];

  const emptyMessage = {
    all: "No reviews yet.",
    published: "No published reviews.",
    flagged: "No flagged reviews.",
    removed: "No removed reviews.",
  }[activeTab];

  const handleView = (id) => navigate("/garage-reviews-detail/" + id);

  const closeDialog = () => {
    if (actionLoadingId) return;
    setDialog(null);
  };

  // Trash icon: fetch removal reasons, then open the remove dialog.
  const handleOpenRemove = async (review) => {
    try {
      const reasons = await fetchRemovalReasons(review.id);

      if (reasons.length === 0) {
        toast.error("No removal reasons were returned by the API.");
        return;
      }

      setDialog({ type: "remove", reviewId: review.id, reasons });
    } catch (error) {
      console.error("Error fetching removal reasons:", error);
      toast.error(error?.message || "Failed to fetch removal reasons.");
    }
  };

  const handleOpenRestore = (review) =>
    setDialog({ type: "restore", reviewId: review.id, reasons: [] });

  const handleDialogConfirm = async ({ reason, note }) => {
    if (!dialog) return;

    const { type, reviewId } = dialog;

    try {
      if (type === "remove") {
        await removeReview(reviewId, reason, note);
        toast.success("Review removed from public profile.");
      } else {
        await restoreReview(reviewId, note);
        toast.success("Review restored to public profile.");
      }

      setDialog(null);
      refresh();
    } catch (error) {
      console.error(`Error during ${type} review:`, error);
      toast.error(error?.message || `Failed to ${type} review.`);
    }
  };

  const tabStyle = (active) => ({
    background: "none",
    border: "none",
    borderBottom: `2px solid ${active ? "#1d4ed8" : "transparent"}`,
    color: active ? "#1d4ed8" : "#475467",
    fontWeight: 600,
    fontSize: 13,
    padding: "8px 4px",
    marginRight: 20,
  });

  return (
    <div className="card h-100 p-0 radius-12 my-3" data-garage-id={garageId}>
      <div className="card-header bg-base py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="d-flex align-items-baseline gap-2 flex-wrap">
          <h6 className="text-lg fw-semibold mb-0">Reviews</h6>
        </div>
        <Link
          to="/garage-reviews"
          className="d-inline-flex align-items-center gap-1 text-primary text-sm fw-medium"
        >
          <Icon icon="mdi:clock-outline" /> Full moderation history
        </Link>
      </div>

      <div className="card-body p-24">
        {/* Stats */}
        <div className="row g-3 mb-24">
          <div className="col-6 col-lg-3">
            <StatCard>
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold" style={{ fontSize: 26 }}>
                  {average.toFixed(1)}
                </span>
                <div>
                  <Stars value={average} size={12} />
                  <div
                    className="text-secondary-light"
                    style={{ fontSize: 11 }}
                  >
                    Public average rating
                  </div>
                </div>
              </div>
            </StatCard>
          </div>
          <div className="col-6 col-lg-3">
            <StatCard>
              <div className="fw-bold" style={{ fontSize: 24 }}>
                {published.length}
              </div>
              <div className="text-secondary-light" style={{ fontSize: 11 }}>
                Published reviews
              </div>
            </StatCard>
          </div>
          <div className="col-6 col-lg-3">
            <StatCard highlight>
              <div
                className="fw-bold"
                style={{ fontSize: 24, color: "#9a5b00" }}
              >
                {flagged.length}
              </div>
              <div style={{ fontSize: 11, color: "#9a5b00" }}>
                Flagged for moderation
              </div>
            </StatCard>
          </div>
          <div className="col-6 col-lg-3">
            <StatCard>
              <div className="fw-bold" style={{ fontSize: 24 }}>
                {removed.length}
              </div>
              <div className="text-secondary-light" style={{ fontSize: 11 }}>
                Removed (kept in history)
              </div>
            </StatCard>
          </div>
        </div>

        {/* Tabs: All -> Published -> Flagged -> Removed */}
        <div className="d-flex align-items-center flex-wrap border-bottom mb-16">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              style={tabStyle(activeTab === tab.value)}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* List (fixed min height, scrolls when there are many reviews) */}
        <div
          style={{
            maxHeight: 520,
            overflowY: "auto",
            paddingRight: 6,
            scrollbarWidth: "thin",
          }}
        >
          {loading ? (
            <div className="text-center text-secondary-light py-4">
              Loading...
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center text-secondary-light py-4">
              {emptyMessage}
            </div>
          ) : (
            <div className="d-flex flex-column gap-12">
              {visible.map((review) => {
                const status = getStatus(review);
                const isFlagged = status === "flagged";
                const isRemoved = status === "removed";
                const flags = getFlags(review);
                const moderation = review.moderation || {};
                const reviewerName =
                  review.reviewer?.fullName ||
                  review.reviewer?.username ||
                  "Anonymous";
                const comment =
                  review.review_text || review.app_review_text || "-";
                const booking = review.booking;
                const bookingId = booking?.id ?? review.booking_id;
                const isActionLoading = actionLoadingId === review.id;
                const chip = statusChip[status];

                return (
                  <div
                    key={review.id}
                    className="d-flex align-items-start gap-3"
                    style={{
                      padding: "12px 16px",
                      borderRadius: 8,
                      border: `1px solid ${isFlagged ? "#f6e2b3" : "#e9ecef"}`,
                      background: isFlagged ? "#fffaf0" : "#fff",
                    }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-semibold"
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        fontSize: 12,
                        background:
                          AVATAR_COLORS[
                            Number(review.id) % AVATAR_COLORS.length
                          ],
                      }}
                    >
                      {getInitials(reviewerName)}
                    </div>

                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <span className="fw-semibold">{reviewerName}</span>
                        <Stars value={review.rating} />
                        <small className="text-secondary-light">
                          {formatDate(review.created_at)}
                        </small>
                        {activeTab === "all" && chip && (
                          <Chip tone={chip.tone}>{chip.label}</Chip>
                        )}
                      </div>
                      <p className="mb-2 text-sm">{comment}</p>

                      <div className="d-flex flex-wrap gap-2">
                        {bookingId ? (
                          <Chip tone={getBookingTone(booking?.status)}>
                            Booking #{bookingId}
                            {booking?.status ? (
                              <span className="text-capitalize">
                                {" "}
                                · {booking.status}
                              </span>
                            ) : null}
                          </Chip>
                        ) : (
                          <Chip tone="danger">
                            No booking found for this account
                          </Chip>
                        )}
                        {flags.map((flag) => (
                          <Chip
                            key={flag}
                            tone="warning"
                            icon="mdi:flag-outline"
                          >
                            {flag}
                          </Chip>
                        ))}
                      </div>

                      {isRemoved && (
                        <div className="mt-2 text-sm">
                          <span
                            className="fw-medium"
                            style={{ color: "#b42318" }}
                          >
                            Removed:{" "}
                            {moderation.removal_reason_label ||
                              moderation.removal_reason ||
                              "No reason provided"}
                          </span>
                          <span className="text-secondary-light">
                            {" "}
                            · by {getRemovedBy(moderation.removed_by)}
                            {moderation.removed_at
                              ? ` on ${formatDate(
                                  moderation.removed_at,
                                  "dd MMM, yyyy 'at' hh:mm a",
                                )}`
                              : ""}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleView(review.id)}
                        title="View details"
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          border: "none",
                          background: "#e0e7ff",
                          color: "#1d4ed8",
                        }}
                      >
                        <Icon icon="lucide:eye" />
                      </button>

                      {!isRemoved ? (
                        <button
                          type="button"
                          onClick={() => handleOpenRemove(review)}
                          disabled={isActionLoading}
                          title="Remove review"
                          className="d-inline-flex align-items-center gap-1"
                          style={{
                            height: 32,
                            padding: "0 12px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            background: "#fdecec",
                            color: "#b42318",
                            border: "1px solid #f5c2c0",
                          }}
                        >
                          <Icon icon="fluent:delete-24-regular" /> Remove
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenRestore(review)}
                          disabled={isActionLoading}
                          title="Restore review"
                          className="d-inline-flex align-items-center gap-1"
                          style={{
                            height: 32,
                            padding: "0 12px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 600,
                            background: "#e8f7ee",
                            color: "#1b7a46",
                            border: "1px solid #bfe6cf",
                          }}
                        >
                          <Icon icon="mdi:restore" /> Restore
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ReviewActionDialog
        isOpen={!!dialog}
        type={dialog?.type}
        reasons={dialog?.reasons || []}
        loading={!!dialog && actionLoadingId === dialog.reviewId}
        onClose={closeDialog}
        onConfirm={handleDialogConfirm}
      />
    </div>
  );
};

export default GarageReviewsSection;
